import { useState, useEffect } from 'react'
import { useAuthStore } from '@/common/zustand/store/auth.store'
import { getDatosPersonales, getAtributosUsuario, getContratosUsuario } from '@/common/utils/services/usuarios.service'
import { getDatosContrato, getEjecutivoContrato } from '@/common/utils/services/contratos.service'
import { getIdUsuario } from '@/auth/auth.utils'
import { esUsuarioAdmin, esUsuarioAdminARToHyS, getRolFromAtributos } from '@/common/utils/perfil/atributos'

/**
 * Cantidad de cuentas efectivas del usuario: solo contratos con estado 'Activo'.
 * Las solicitudes de asociación pendientes ('Pendiente') se muestran en "Mis cuentas",
 * pero no habilitan el selector de cuenta para un usuario con un solo contrato activo.
 *
 * El endpoint /usuarios/contratos devuelve claves camelCase (estado, contrato, ...),
 * ver mapearContrato en administracion-usuario_service.
 */
export const contarContratosActivos = (contratos) => contratos.filter((c) => c.estado === 'Activo').length

export const usePerfilUsuario = () => {
  const { contrato } = useAuthStore()

  const [datosPerfil, setDatosPerfil] = useState({
    nombre: '',
    apellido: '',
    nombreConcatenado: '',
    rol: '',
    empresa: '',
    cuit: '',
    numeroContrato: '',
    ejecutivo: '',
    emailEjecutivo: '',
    cantidadContratos: 0,
    esAdmin: false,
    esAdminARToHyS: false,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchDatosPerfil = async () => {
      const idUsuario = getIdUsuario()

      if (!idUsuario) {
        setError('No se encontró el ID de usuario')
        return
      }

      if (!contrato) {
        setError('No hay contrato activo')
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // Obtener datos en paralelo
        const [resultDatosPersonales, resultDatosContrato, resultEjecutivo, resultAtributos, resultContratos] =
          await Promise.all([
            getDatosPersonales(Number(idUsuario)),
            getDatosContrato(contrato),
            getEjecutivoContrato(contrato),
            getAtributosUsuario(Number(idUsuario), 1, Number(contrato)),
            getContratosUsuario(Number(idUsuario)),
          ])

        setDatosPerfil((prev) => {
          const newDatosPerfil = { ...prev }

          // Procesar datos personales del usuario
          if (resultDatosPersonales.code === 200 && resultDatosPersonales.data) {
            const { nombre, apellido, nombreConcatenado } = resultDatosPersonales.data
            newDatosPerfil.nombre = nombre || ''
            newDatosPerfil.apellido = apellido || ''
            newDatosPerfil.nombreConcatenado = nombreConcatenado || ''

            // Fallback: si no hay nombre ni apellido, usar nombreConcatenado
            if (!newDatosPerfil.nombre && !newDatosPerfil.apellido && newDatosPerfil.nombreConcatenado) {
              newDatosPerfil.nombre = newDatosPerfil.nombreConcatenado
              newDatosPerfil.apellido = ''
            }
          }

          // Procesar atributos para obtener el rol y verificar si es admin
          if (resultAtributos.code === 200 && resultAtributos.data) {
            newDatosPerfil.rol = getRolFromAtributos(resultAtributos.data)
            newDatosPerfil.esAdmin = esUsuarioAdmin(resultAtributos.data)
            newDatosPerfil.esAdminARToHyS = esUsuarioAdminARToHyS(resultAtributos.data)
          }

          // Procesar cantidad de contratos
          if (resultContratos.code === 200 && resultContratos.data) {
            newDatosPerfil.cantidadContratos = contarContratosActivos(resultContratos.data)
          }

          // Procesar datos del contrato
          if (resultDatosContrato.code === 200 && resultDatosContrato.data) {
            newDatosPerfil.empresa = resultDatosContrato.data.NOMBREEMPRESA || ''
            newDatosPerfil.cuit = resultDatosContrato.data.CUIT || ''
            newDatosPerfil.numeroContrato = String(resultDatosContrato.data.CONTRATO || contrato)
          }

          // Procesar datos del ejecutivo
          if (resultEjecutivo.code === 200 && resultEjecutivo.data) {
            newDatosPerfil.ejecutivo = resultEjecutivo.data.NOMBRE || ''
            newDatosPerfil.emailEjecutivo = resultEjecutivo.data.MAIL || ''
          }

          return newDatosPerfil
        })
      } catch (err) {
        setError('Error al cargar los datos del perfil')
        console.error('Error en usePerfilUsuario:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDatosPerfil()
  }, [contrato]) // Se re-ejecuta cuando cambia el contrato

  return {
    ...datosPerfil,
    isLoading,
    error,
  }
}
